<template>
  <div class="chat-container" :class="showChat ? 'bottom-[30px] w-[calc(100% - 6px)] left-[2px]' : ''">
    <!-- Floating Button (unchanged) -->
    <button
      @click="toggleChat"
      @mousemove="handleMouseMove"
      @mouseleave="resetPosition"
      :class="{ 'button-active': showChat, 'bottom-[4.5rem]': !showChat }"
      class="max-sm:!right-0 !bottom-5 !right-5 siri-button glassmorphic-button bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
      v-if="shouldShow"
      :style="{ '--hue': currentHue }"
      ref="button"
      aria-label="Toggle AI Assistant"
    >
      <span 
        class="button-text text-black glow"
        :style="{ transform: `translate(${innerPos.x}px, ${innerPos.y}px)` }"
      >
        {{ active ? '×' : ' ' }}
      </span>
    </button>

    <!-- Chat Interface -->
    <transition name="slide-up">
      <div v-if="showChat" class="chat-interface max-sm:!max-h-full max-sm:!top-0" role="dialog" aria-labelledby="chat-header">
        <div class="chat-header">
          <div class="header-content">
            <h3 id="chat-header">AI Assistant</h3>
            <p>Trained to answer your questions.</p>
          </div>
          <button @click="toggleChat" class="elegant-close-button" aria-label="Close chat">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        
        <div ref="messagesContainer" class="chat-messages" role="log" aria-live="polite">
          <div 
            v-for="(message) in messages" 
            :key="message.id"
            :class="['message', message.sender]"
          >
            <div class="message-content">
              <!-- Image display for cat API URLs -->
              <img 
                v-if="isImageUrl(message.text)" 
                :src="message.text" 
                alt="AI response image"
                class="chat-image"
                @load="scrollToBottom"
              >
              <!-- Regular text message -->
<!-- In your template, replace the simple message.text display with: -->
<template v-else>
  <span 
    v-for="(word, index) in message.text.split(' ')" 
    :key="index"
    class="word"
    :style="{'--word-index': index}"
  >
    {{ word }}&nbsp;
  </span>
</template>
            </div>
            <div class="message-time">
              {{ formatTime(message.timestamp) }}
            </div>
          </div>
          
          <!-- Enhanced typing indicator -->
          <div v-if="isLoadingAnswer" class="message ai">
            <div class="message-content typing-indicator">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
        </div>
        
        <div class="chat-input">
          <input
            v-model="userInput"
            @keyup.enter="sendMessage"
            placeholder="Type your message..."
            :disabled="isLoadingAnswer || !!aiError"
            aria-label="Type your message"
          />
          <button @click="sendMessage" class="send-button" :disabled="isLoadingAnswer || !!aiError" aria-label="Send message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 2L11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  data() {
    return {
      showChat: false,
      shouldShow: true,
      userInput: '',
      innerPos: { x: 0, y: 0 },
      maxMovement: 10,
      messages: [],
      isLoadingAnswer: false,
      aiError: null,
      active: false,
      currentHue: Math.floor(Math.random() * 360),
    };
  },
  async mounted() {
    this.colorShift();
    try {
      if (!window.aiLightReadyPromise) {
        throw new Error('AI script (aiLight.js) not loaded');
      }
      await window.aiLightReadyPromise;
      this.messages.push({
        id: 'initial-greeting',
        text: "Hi there! I'm an AI trained to answer your questions. How can I help?",
        sender: 'ai',
        timestamp: new Date()
      });
      console.log("ChatButton: AI Light Module is ready!");
    } catch (error) {
      console.error("ChatButton: Error initializing AI Light Module:", error);
      this.aiError = "Failed to initialize AI. Please ensure aiLight.js is loaded and try refreshing the page.";
      this.messages.push({
        id: 'error',
        text: this.aiError,
        sender: 'ai',
        timestamp: new Date()
      });
    }
  },
  methods: {
    isImageUrl(text) {
      return text.startsWith('http') && 
             (text.includes('cataas.com/cat') || 
              text.match(/\.(jpg|jpeg|png|gif)$/i));
    },
    handleMouseMove(event) {
      if (!this.$refs.button) return;
      
      const button = this.$refs.button;
      const rect = button.getBoundingClientRect();
      
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      const relX = (mouseX - centerX) / centerX;
      const relY = (mouseY - centerY) / centerY;
      
      this.innerPos = {
        x: Math.max(-this.maxMovement, Math.min(this.maxMovement, relX * this.maxMovement)),
        y: Math.max(-this.maxMovement, Math.min(this.maxMovement, relY * this.maxMovement))
      };
    },
    resetPosition() {
      this.innerPos = { x: 0, y: 0 };
    },
    toggleChat() {
      this.showChat = !this.showChat;
      this.active = this.showChat;
      if (!this.showChat) {
        setTimeout(() => { this.shouldShow = true; }, 300);
      } else {
        this.shouldShow = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
        if (this.aiError) {
          this.messages = [{ id: 'error', text: this.aiError, sender: 'ai', timestamp: new Date() }];
        } else if (this.messages.length === 0) {
          this.messages.push({
            id: 'greeting',
            text: "Hello! How can I assist you today?",
            sender: 'ai',
            timestamp: new Date()
          });
        }
      }
    },
    colorShift() {
      this.colorInterval = setInterval(() => {
        this.currentHue = (this.currentHue + 1) % 360;
      }, 15);
    },
    async sendMessage() {
      if (!this.userInput.trim() || this.isLoadingAnswer || this.aiError) return;
      
      const userMessageText = this.userInput.trim();
      this.messages.push({
        id: `${Date.now()}-user`,
        text: userMessageText,
        sender: 'user',
        timestamp: new Date()
      });
      
      this.userInput = '';
      this.isLoadingAnswer = true;
      
      this.$nextTick(() => {
        this.scrollToBottom();
      });

      try {
        if (!window.aiLightModule || !window.aiLightModule.getAnswer) {
          throw new Error('aiLightModule or generateAnswer function is not available on window');
        }
        const aiResponseText = await window.aiLightModule.getAnswer(userMessageText);
        this.messages.push({
          id: `${Date.now()}-ai`,
          text: aiResponseText,
          sender: 'ai',
          timestamp: new Date()
        });
      } catch (error) {
        console.error("ChatButton: Error getting AI response:", error);
        this.messages.push({
          id: `${Date.now()}-ai`,
          text: "I encountered an issue while processing your request. Please try again.",
          sender: 'ai',
          timestamp: new Date()
        });
        this.aiError = "Error communicating with the AI module.";
      } finally {
        this.isLoadingAnswer = false;
        this.$nextTick(() => {
        });
      }
    },
    scrollToBottom() {
      requestAnimationFrame(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    formatTime(date) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  },
  beforeUnmount() {
    if (this.colorInterval) {
      clearInterval(this.colorInterval);
    }
  }
};
</script>

<style lang="scss" scoped>

.message.ai .message-content {
  overflow: hidden;
}

.message.ai .message-content span.word {
  opacity: 0;
  filter: blur(4px);
  display: inline-block;
  animation: fadeInWord 0.3s forwards;
  animation-delay: calc(var(--word-index) * 0.05s);
}

@keyframes fadeInWord {
  to {
    opacity: 1;
    filter: blur(0);
  }
}

/* Ensure spaces are preserved between words */
.message.ai .message-content {
  white-space: pre-wrap;
}
.chat-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 15px;
}

@media (max-width: 639.98px) {
  .chat-container {
    position: fixed;
    bottom: 5px;
      max-height: calc(100vh - 10px);
  pointer-events: none;
  z-index: 1000;
    right: 3px;
    width: calc(100vw - 6px);
    left: 2px;
  }
  
   /* Chat Interface */
   .chat-interface {
    width: 100% !important;
    max-width: 100% !important;
    min-height: 500px;
    pointer-events: all;
    height: auto !important;
    max-height: calc(100vh - 10px);
    background: rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
}

/* Glassmorphic Button Styles */
.glassmorphic-button {
  width: 36px;  /* ~w-5 equivalent */
  height: 36px;  /* ~h-5 equivalent */
  min-width: 36px;
  min-height: 36px;
  pointer-events: all;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 16px 0 rgba(31, 38, 135, 0.2);
  color: white;
  font-size: 14px;
  font-weight: 600;
}

.button-text {
  font-size: 14px;
  font-weight: bold;
  text-shadow: 0 0 4px rgba(255,255,255,0.5);
  width: 30px;  /* ~w-5 equivalent */
  height: 30px;  /* ~h-5 equivalent */
  min-width: 30px;
  min-height: 30px;
  border-radius: 50%;
  border: none;
  box-shadow: 
    0 0 10px hsla(var(--hue), 80%, 60%, 0.3),
    inset 0 0 5px hsla(var(--hue), 100%, 80%, 0.2);
  overflow: hidden;
  z-index: 1000;
  --hue: 220;
  opacity: .9;
  display: flex;
  align-items: center;
  justify-content: center;
}


.glassmorphic-button:hover {
  box-shadow: 0 12px 40px 0 rgba(31, 38, 135, 0.3);
}

.glassmorphic-button:active {
  transform: scale(0.95);
}

.glassmorphic-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('../assets/logo.png');
  /* Prevent the image from repeating */
  background-repeat: no-repeat;
  /* Scale the image to cover the entire div */
  background-size: 50%; /* Options: cover, contain, auto, or specific dimensions */
  background-color: rgba(55, 65, 81, 0.5);
  /* Center the image within the div */
  background-position: center center;
  border-radius: 50%;
  z-index: -1;
  &:hover {
    background-color: rgb(75 85 99 / 0.5);
  }
}


.button-text {
  opacity: 0.9;
  pointer-events: all;

}

.button-icon {
  font-size: 24px;
  line-height: 1;
}

/* Chat Interface Styles */
/* Elegant Close Button */
.elegant-close-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-left: auto;
  
}

.elegant-close-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  transform: rotate(90deg);
}

.elegant-close-button svg {
  transition: all 0.3s ease;
}

/* Chat Header */
.chat-header {
  display: flex;
  pointer-events: all;
  align-items: center;
  padding: 15px 20px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-content {
  flex: 1;
}

.chat-header h3 {
  margin: 0;
  color: white;
  font-size: 18px;
}

.chat-header p {
  margin: 5px 0 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

/* Chat Interface */
.chat-interface {
  width: 350px;
  max-width: 90vw;
  height: 500px;
  pointer-events: all;

  max-height: 70vh;
  background: rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Animations */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.chat-messages {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.message {
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  max-width: 80%;
}

.message.user {
  align-items: flex-end;
  margin-left: auto;
}

.message.ai {
  align-items: flex-start;
  margin-right: auto;
}

.message-content {
  padding: 10px 15px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
}

.message.user .message-content {
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  border-bottom-right-radius: 5px;
}

.message.ai .message-content {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-bottom-left-radius: 5px;
}

.message-time {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 4px;
}

.typing-indicator {
  font-style: italic;
  color: rgba(255, 255, 255, 0.7);
  padding: 10px 15px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.15);
  align-self: flex-start;
  margin-right: auto;
  border-bottom-left-radius: 5px;
}


.chat-input {
  pointer-events: all;
  display: flex;
  padding: 15px;
  background: rgba(0, 0, 0, 0.26);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.chat-input input {
  flex: 1;
  padding: 12px 15px;
  border: none;
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 14px;
  outline: none;
}

.chat-input input:disabled {
  background-color: rgba(255, 255, 255, 0.1);
  cursor: not-allowed;
}


.chat-input input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.send-button {
  width: 40px;
  height: 40px;
  margin-left: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.send-button:hover {
  background: rgba(255, 255, 255, 0.4);
}

.send-button:active {
  transform: scale(0.95);
}

.send-button:disabled {
  background-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.4);
  cursor: not-allowed;
}


/* Animations */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

/* Scrollbar styling */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.4);
}



/* */

.siri-button {
  position: fixed;
  width: 36px;  /* ~w-5 equivalent */
  height: 36px;  /* ~h-5 equivalent */
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  border: none;
  background-image: url('../assets/askai.jpeg');
  background-size: cover;
  background-position: center;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 
    0 0 10px hsla(var(--hue), 80%, 60%, 0.3),
    inset 0 0 5px hsla(var(--hue), 100%, 80%, 0.2);
  cursor: pointer;
  overflow: hidden;
  z-index: 1000;
  transition: all 0.3s ease;
  background: hsla(var(--hue), 80%, 60%, 0.05);
  --hue: 220;
  opacity: .9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.glow {
  cursor: pointer;
  pointer-events: all;

  color: lightblue;
  font-weight: bold;
  overflow: hidden;
  z-index: 1000;
  transition: all 0.3s ease;
  transform: scale(var(--wave-scale));
  background: hsla(var(--hue), 80%, 60%, 0.05);
  box-shadow: 
    0 0 20px hsla(var(--hue), 80%, 60%, 0.3),
    inset 0 0 10px hsla(var(--hue), 100%, 80%, 0.52);
    --hue: 220; /* Default hue - change as needed */
  animation: 
    shadow-pulse 4s ease infinite,
    scale-change 5s linear infinite,
    hue-rotate 15s linear infinite;
    opacity: .9;

}

.siri-button:hover {
  box-shadow: 
    0 0 20px hsla(var(--hue), 80%, 60%, 0.3),
    inset 0 0 10px hsla(var(--hue), 100%, 80%, 0.52);
    --hue: 220; /* Default hue - change as needed */
  animation: 
    shadow-pulse 4s ease infinite,
    hue-rotate 15s linear infinite;
}


@keyframes shadow-pulse {
  0%, 100% {
    box-shadow:
      0 0 15px hsla(var(--hue), 80%, 60%, 0.4),
      inset 0 0 8px hsla(var(--hue), 100%, 80%, 0.6);
  }
  50% {
    box-shadow:
      0 0 25px hsla(var(--hue), 80%, 70%, 0.6),
      inset 0 0 12px hsla(var(--hue), 100%, 90%, 0.8),
      0 0 5px white;
  }
}

@keyframes hue-rotate {
  0% {
    --hue: 0; /* Full color cycle */
    
  }
  50% {
    --hue: 360; /* Full color cycle */
  }
  100% {
    --hue: 0; /* Full color cycle */
    
  }
}
@keyframes scale-change {
  0% {
    scale: .95; 
    
  }
  50% {
    scale: .9;
  }
  100% {
    scale: .95; 
    
  }
}

@property --hue {
  syntax: "<number>";
  inherits: true;
  initial-value: 220;
}

.waves {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-bottom: 15%;
  gap: 5%;
}

.wave {
  width: 8%;
  height: calc(var(--wave-height) * 100%);
  background: hsla(var(--hue), 100%, 70%, var(--wave-opacity));
  border-radius: 50% 50% 0 0;
  animation: wave-animation 1.5s ease-in-out infinite alternate;
  animation-delay: var(--wave-delay);
  transform-origin: bottom;
  filter: brightness(1.2);
  transition: all 0.5s ease;
}

@keyframes wave-animation {
  0% {
    transform: scaleY(0.7) translateY(10%);
  }
  50% {
    transform: scaleY(1.1) translateY(-5%);
  }
  100% {
    transform: scaleY(0.9) translateY(5%);
  }
}

.button-icon {
  position: relative;
  z-index: 2;
  color: white;
  font-size: 24px;
  font-weight: bold;
  text-shadow: 0 0 10px hsla(var(--hue), 100%, 50%, 0.5);
  transition: all 0.3s ease;
}

.siri-button:hover .button-icon {
  transform: scale(1.2);
}

/* Distortion effect on hover */
.siri-button::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  right: -50%;
  bottom: -50%;
  background: radial-gradient(
    circle at center,
    hsla(var(--hue), 100%, 70%, 0.1) 0%,
    transparent 70%
  );
  transform: scale(1.5);
  transition: all 0.5s ease;
  opacity: 0;
}

.siri-button:hover::after {
  opacity: 1;
  animation: distort 3s infinite linear;
}

@keyframes distort {
  0% {
    transform: scale(1.5) rotate(0deg);
  }
  25% {
    transform: scale(1.6) rotate(5deg);
  }
  50% {
    transform: scale(1.7) rotate(0deg);
  }
  75% {
    transform: scale(1.6) rotate(-5deg);
  }
  100% {
    transform: scale(1.5) rotate(0deg);
  }
}


.siri-button {
  /* your existing styles */
  position: relative; /* Ensure this is set */
}

.button-text {
  position: absolute;
  transform: translate(-50%, -50%);
  transition: transform 0.2s ease-out;
  will-change: transform; /* Optimize for animation */
  
  /* your existing glow styles */
}

.ai-status {
  padding: 10px;
  text-align: center;
  font-size: 0.9em;
  color: rgba(255, 255, 255, 0.7);
  background-color: rgba(0,0,0,0.1); /* Slightly different background for status */
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.ai-status.error {
  color: #ff6b6b; /* A reddish color for errors */
  font-weight: bold;
}
.chat-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 15px;
}

@media (max-width: 639.98px) {
  .chat-container {
    position: fixed;
    bottom: 5px;
      max-height: calc(100vh - 10px);
  pointer-events: none;
  z-index: 1000;
    right: 3px;
    width: calc(100vw - 6px);
    left: 2px;
  }
  
   /* Chat Interface */
   .chat-interface {
    width: 100% !important;
    max-width: 100% !important;
    min-height: 500px;
    pointer-events: all;
    height: auto !important;
    max-height: calc(100vh - 10px);
    background: rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
}

/* Glassmorphic Button Styles */
.glassmorphic-button {
  width: 36px;  /* ~w-5 equivalent */
  height: 36px;  /* ~h-5 equivalent */
  min-width: 36px;
  min-height: 36px;
  pointer-events: all;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 16px 0 rgba(31, 38, 135, 0.2);
  color: white;
  font-size: 14px;
  font-weight: 600;
}

.button-text {
  font-size: 14px;
  font-weight: bold;
  text-shadow: 0 0 4px rgba(255,255,255,0.5);
  width: 30px;  /* ~w-5 equivalent */
  height: 30px;  /* ~h-5 equivalent */
  min-width: 30px;
  min-height: 30px;
  border-radius: 50%;
  border: none;
  box-shadow: 
    0 0 10px hsla(var(--hue), 80%, 60%, 0.3),
    inset 0 0 5px hsla(var(--hue), 100%, 80%, 0.2);
  overflow: hidden;
  z-index: 1000;
  --hue: 220;
  opacity: .9;
  display: flex;
  align-items: center;
  justify-content: center;
}


.glassmorphic-button:hover {
  box-shadow: 0 12px 40px 0 rgba(31, 38, 135, 0.3);
}

.glassmorphic-button:active {
  transform: scale(0.95);
}

.glassmorphic-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('../assets/logo.png');
  /* Prevent the image from repeating */
  background-repeat: no-repeat;
  /* Scale the image to cover the entire div */
  background-size: 50%; /* Options: cover, contain, auto, or specific dimensions */
  background-color: rgba(55, 65, 81, 0.5);
  /* Center the image within the div */
  background-position: center center;
  border-radius: 50%;
  z-index: -1;
  &:hover {
    background-color: rgb(75 85 99 / 0.5);
  }
}


.button-text {
  opacity: 0.9;
  pointer-events: all;

}

.button-icon {
  font-size: 24px;
  line-height: 1;
}

/* Chat Interface Styles */
/* Elegant Close Button */
.elegant-close-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-left: auto;
  
}

.elegant-close-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  transform: rotate(90deg);
}

.elegant-close-button svg {
  transition: all 0.3s ease;
}

/* Chat Header */
.chat-header {
  display: flex;
  pointer-events: all;
  align-items: center;
  padding: 15px 20px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-content {
  flex: 1;
}

.chat-header h3 {
  margin: 0;
  color: white;
  font-size: 18px;
}

.chat-header p {
  margin: 5px 0 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

/* Chat Interface */
.chat-interface {
  width: 350px;
  max-width: 90vw;
  height: 500px;
  pointer-events: all;

  max-height: 70vh;
  background: rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Animations */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.chat-messages {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.message {
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  max-width: 80%;
}

.message.user {
  align-items: flex-end;
  margin-left: auto;
}

.message.ai {
  align-items: flex-start;
  margin-right: auto;
}

.message-content {
  padding: 10px 15px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
}

.message.user .message-content {
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  border-bottom-right-radius: 5px;
}

.message.ai .message-content {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-bottom-left-radius: 5px;
}

.message-time {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 4px;
}

.chat-input {
  pointer-events: all;
  display: flex;
  padding: 15px;
  background: rgba(0, 0, 0, 0.26);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.chat-input input {
  flex: 1;
  padding: 12px 15px;
  border: none;
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 14px;
  outline: none;
}

.chat-input input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.send-button {
  width: 40px;
  height: 40px;
  margin-left: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.send-button:hover {
  background: rgba(255, 255, 255, 0.4);
}

.send-button:active {
  transform: scale(0.95);
}

/* Animations */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

/* Scrollbar styling */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.4);
}



/* */

.siri-button {
  position: fixed;
  width: 36px;  /* ~w-5 equivalent */
  height: 36px;  /* ~h-5 equivalent */
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  border: none;
  background-image: url('../assets/askai.jpeg');
  background-size: cover;
  background-position: center;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 
    0 0 10px hsla(var(--hue), 80%, 60%, 0.3),
    inset 0 0 5px hsla(var(--hue), 100%, 80%, 0.2);
  cursor: pointer;
  overflow: hidden;
  z-index: 1000;
  transition: all 0.3s ease;
  background: hsla(var(--hue), 80%, 60%, 0.05);
  --hue: 220;
  opacity: .9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.glow {
  cursor: pointer;
  pointer-events: all;

  color: lightblue;
  font-weight: bold;
  overflow: hidden;
  z-index: 1000;
  transition: all 0.3s ease;
  transform: scale(var(--wave-scale));
  background: hsla(var(--hue), 80%, 60%, 0.05);
  box-shadow: 
    0 0 20px hsla(var(--hue), 80%, 60%, 0.3),
    inset 0 0 10px hsla(var(--hue), 100%, 80%, 0.52);
    --hue: 220; /* Default hue - change as needed */
  animation: 
    shadow-pulse 4s ease infinite,
    scale-change 5s linear infinite,
    hue-rotate 15s linear infinite;
    opacity: .9;

}

.siri-button:hover {
  box-shadow: 
    0 0 20px hsla(var(--hue), 80%, 60%, 0.3),
    inset 0 0 10px hsla(var(--hue), 100%, 80%, 0.52);
    --hue: 220; /* Default hue - change as needed */
  animation: 
    shadow-pulse 4s ease infinite,
    hue-rotate 15s linear infinite;
}


@keyframes shadow-pulse {
  0%, 100% {
    box-shadow:
      0 0 15px hsla(var(--hue), 80%, 60%, 0.4),
      inset 0 0 8px hsla(var(--hue), 100%, 80%, 0.6);
  }
  50% {
    box-shadow:
      0 0 25px hsla(var(--hue), 80%, 70%, 0.6),
      inset 0 0 12px hsla(var(--hue), 100%, 90%, 0.8),
      0 0 5px white;
  }
}

@keyframes hue-rotate {
  0% {
    --hue: 0; /* Full color cycle */
    
  }
  50% {
    --hue: 360; /* Full color cycle */
  }
  100% {
    --hue: 0; /* Full color cycle */
    
  }
}
@keyframes scale-change {
  0% {
    scale: .95; 
    
  }
  50% {
    scale: .9;
  }
  100% {
    scale: .95; 
    
  }
}

@property --hue {
  syntax: "<number>";
  inherits: true;
  initial-value: 220;
}

.waves {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-bottom: 15%;
  gap: 5%;
}

.wave {
  width: 8%;
  height: calc(var(--wave-height) * 100%);
  background: hsla(var(--hue), 100%, 70%, var(--wave-opacity));
  border-radius: 50% 50% 0 0;
  animation: wave-animation 1.5s ease-in-out infinite alternate;
  animation-delay: var(--wave-delay);
  transform-origin: bottom;
  filter: brightness(1.2);
  transition: all 0.5s ease;
}

@keyframes wave-animation {
  0% {
    transform: scaleY(0.7) translateY(10%);
  }
  50% {
    transform: scaleY(1.1) translateY(-5%);
  }
  100% {
    transform: scaleY(0.9) translateY(5%);
  }
}

.button-icon {
  position: relative;
  z-index: 2;
  color: white;
  font-size: 24px;
  font-weight: bold;
  text-shadow: 0 0 10px hsla(var(--hue), 100%, 50%, 0.5);
  transition: all 0.3s ease;
}

.siri-button:hover .button-icon {
  transform: scale(1.2);
}

/* Distortion effect on hover */
.siri-button::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  right: -50%;
  bottom: -50%;
  background: radial-gradient(
    circle at center,
    hsla(var(--hue), 100%, 70%, 0.1) 0%,
    transparent 70%
  );
  transform: scale(1.5);
  transition: all 0.5s ease;
  opacity: 0;
}

.siri-button:hover::after {
  opacity: 1;
  animation: distort 3s infinite linear;
}

@keyframes distort {
  0% {
    transform: scale(1.5) rotate(0deg);
  }
  25% {
    transform: scale(1.6) rotate(5deg);
  }
  50% {
    transform: scale(1.7) rotate(0deg);
  }
  75% {
    transform: scale(1.6) rotate(-5deg);
  }
  100% {
    transform: scale(1.5) rotate(0deg);
  }
}


.siri-button {
  /* your existing styles */
  position: relative; /* Ensure this is set */
}

.button-text {
  position: absolute;
  transform: translate(-50%, -50%);
  transition: transform 0.2s ease-out;
  will-change: transform; /* Optimize for animation */
  
  /* your existing glow styles */
}
.message.ai .typing-indicator {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  color: #666;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #666;
  margin: 0 3px;
  animation: bounce 1.4s infinite ease-in-out;
}

.dot:nth-child(1) {
  animation-delay: 0s;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-5px);
  }
}

.chat-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  margin-top: 5px;
  display: block;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Message bubble animation */
.message {
  opacity: 0;
  transform: translateY(10px);
  animation: messageAppear 0.3s ease-out forwards;
}

@keyframes messageAppear {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
/* Keep your other existing styles */
</style>